<template>
  <div class="svoi-file-picker-complex">
    <div v-if="header || tooltip" class="header">
      <div class="label">
        <span>{{ header }}</span>
        <span v-if="required" class="-required">*</span>
      </div>
      <SvoiTooltippedIcon v-if="tooltip" :icon="iconTooltip" class="tooltip" size="s" />
    </div>
    <Component :is="component" v-bind="$props" v-model="uploadFiles" :disabled="disableInput">
      <template v-for="(_, slot) of $slots" #[slot]="scope">
        <slot :name="slot" v-bind="scope" />
      </template>
    </Component>
    <Component
      :is="componentList"
      v-if="!hideList && propValueLength > 0"
      v-model="propValue"
      :disabled="disabled"
      @on-delete-file="onClickDeleteFile"
    />
  </div>
</template>

<script lang="ts">
import { ComponentPropsOptions, computed, defineComponent, isVue2, PropType, ref, watch } from 'vue-demi'
import {
  FileData,
  Mode,
  ModeEnum,
  PropValueType,
  Type,
  TypeEnum,
  TypeList,
  TypeListEnum
} from '@svoi-ui/interfaces/file-picker'
import type { IconType } from '@svoi-ui/interfaces/icon'
import { SvoiFilesList, SvoiImagesList } from '@svoi-ui/components/list'
import { SvoiIcon, SvoiTooltippedIcon } from '@svoi-ui/components/icon'
import { PositionsEnum as TooltipPositionsEnum } from '@svoi-ui/interfaces/tooltip'
import { isString } from '@svoi-ui/shared/utils/guards'
import { BaseFilePickerComponent } from './BaseFilePicker'
import { SvoiFilePicker, SvoiFilePickerButton } from '.'

const modelProp: ComponentPropsOptions = isVue2
  ? {
      value: {
        type: Array as PropType<PropValueType>,
        default: null
      }
    }
  : {
      modelValue: {
        type: Array as PropType<PropValueType>,
        default: null
      }
    }

export default defineComponent({
  name: 'SvoiFilePickerComplex',
  components: { SvoiFilePicker, SvoiFilePickerButton, SvoiFilesList, SvoiImagesList, SvoiTooltippedIcon, SvoiIcon },
  props: {
    ...BaseFilePickerComponent.props,
    ...modelProp,
    mode: {
      type: String as PropType<Mode | string>,
      default: ModeEnum.append,
      validator: (value: unknown) => {
        return isString(value) && [ModeEnum.append.toString(), ModeEnum.replace.toString()].includes(value)
      }
    },
    type: {
      type: String as PropType<Type | string>,
      default: TypeEnum.dragArea,
      validator: (value: unknown) => {
        return isString(value) && [TypeEnum.button.toString(), TypeEnum.dragArea.toString()].includes(value)
      }
    },
    buttonText: {
      type: String,
      default: 'Загрузить файлы'
    },
    typeList: {
      type: String as PropType<TypeList | string>,
      default: TypeListEnum.default,
      validator: (value: unknown) => {
        return isString(value) && [TypeListEnum.default.toString(), TypeListEnum.images.toString()].includes(value)
      }
    },
    header: {
      type: String,
      default: ''
    },
    tooltip: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    hideList: {
      type: Boolean,
      default: false
    }
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const uploadFiles = ref<Array<File>>([])
    const propValue = computed<Array<File | FileData>>({
      get() {
        // @ts-expect-error
        return (isVue2 ? props.value : props.modelValue) ?? []
      },
      set(value) {
        emit(isVue2 ? 'input' : 'update:modelValue', value)
      }
    })
    const propValueLength = computed(() => propValue.value.length)
    const disableInput = computed(() => {
      if (props.disabled) {
        return true
      }

      if (props.mode === ModeEnum.replace) {
        return false
      }

      return props.multiple ? propValueLength.value >= props.maxCount : propValueLength.value > 0
    })
    const component = computed(() => {
      switch (props.type) {
        case TypeEnum.button:
          return SvoiFilePickerButton
        case TypeEnum.dragArea:
        default:
          return SvoiFilePicker
      }
    })
    const componentList = computed(() => {
      switch (props.typeList) {
        case TypeListEnum.images:
          return SvoiImagesList
        case TypeListEnum.default:
        default:
          return SvoiFilesList
      }
    })
    const iconTooltip = computed<IconType>(() => ({
      icon: 'essential/info-circle',
      tooltip: props.tooltip,
      tooltipPosition: TooltipPositionsEnum.bottomRight
    }))

    const onClickDeleteFile = (file: File | FileData) => {
      let newValue: PropValueType = propValue.value.filter(i => i !== file)
      if (newValue.length === 0 && !props.multiple) {
        newValue = null
      }

      emit(isVue2 ? 'input' : 'update:modelValue', newValue)
    }

    const compareFiles = (file1: File, file2: File): boolean => {
      return file1.name === file2.name && file1.size === file2.size && file1.lastModified === file2.lastModified
    }

    const mergeFiles = () => {
      const res = [...propValue.value]
      let counter = res.length
      uploadFiles.value
        .filter(f => !res.find(i => i instanceof File && compareFiles(f, i)))
        .forEach(f => {
          if (counter >= props.maxCount) {
            return
          }
          res.push(f)
          counter++
        })
      return res
    }

    watch(
      () => uploadFiles.value,
      () => {
        if (uploadFiles.value.length > 0) {
          if (props.mode === ModeEnum.replace) {
            emit(isVue2 ? 'input' : 'update:modelValue', uploadFiles.value)
          } else {
            const res = mergeFiles()
            emit(isVue2 ? 'input' : 'update:modelValue', res)
          }

          uploadFiles.value = []
        }
      }
    )

    return {
      propValue,
      uploadFiles,
      disableInput,
      component,
      componentList,
      iconTooltip,
      propValueLength,
      onClickDeleteFile
    }
  }
})
</script>

<style lang="scss" scoped>
@use '@svoi-ui/assets/scss/components/file-picker.scss';
</style>
